import React, { FormEvent, useCallback, useMemo, useState } from 'react';
import styles from './forgotPasswordConfirm.module.scss';
import { TextInput, TextInputValue } from '../../components/text-input/TextInput';
import { useQueryParams } from '../../hooks/useQueryParams';
import { NavLink, useHistory } from 'react-router-dom';
import { validatePassword } from '../../utils/validation';
import NewAuthPageLayout from '@src/components/auth-page-layout/NewAuthPageLayout';
import Button from '@src/components/button/Button';
import ArrowIcon from '../../assets/icons/curvearrow.svg?react';
import { updateIdpV2Password } from '@src/utils/idpV2Auth';

type Props = {};

function ForgotPasswordConfirm(props: Props) {
  const userId = useQueryParams().get('user_id');
  const hash = useQueryParams().get('hash');
  const code = useQueryParams().get('code');
  const history = useHistory();

  const [clientError, setClientError] = useState<undefined | string>();
  const [serverError, setServerError] = useState<undefined | string>();
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const hasRequiredParams = !!userId && !!hash && !!code;

  const handleChange = useCallback((setter) => (value: TextInputValue) => {
    setClientError(undefined);
    setServerError(undefined);
    setter(value);
  }, []);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    if (!userId || !hash || !code) {
      return setClientError('This password reset link is invalid. Please request a new link.');
    }

    const error = validatePassword(password);
    if (error) {
      return setClientError(error);
    }

    if (password !== confirmPassword) {
      return setClientError('The password and the confirm password should match.')
    }

    try {
      setLoading(true);
      setServerError(undefined);
      await updateIdpV2Password({ user_id: userId, hash, code, password });
      history.push('/login?resetPassword=true')
    } catch (e) {
      setServerError((e as Error).message);
      setCompleted(true);
    } finally {
      setLoading(false);
    }
  }, [password, confirmPassword, userId, hash, code, history]);

  const completedContent = useMemo(() => {
    if (!completed) return null;
    if (serverError) {
      return (
        <div className={styles.confirm}>
          <div className={styles.confirmErrorTitle}>Error!</div>
          <div className={styles.confirmSubtitle}>{serverError}</div>
          <div className={styles.footer}>
            Back to <NavLink className={styles.footerLink} to={'/login'}>Login</NavLink>
          </div>
        </div>
      );
    }
    return (
      <div className={styles.confirm}>
        <div className={styles.confirmTitle}>Your password has been set successfully!</div>
        <div/>
        <div className={styles.footer}>
          Back to <NavLink className={styles.footerLink} to={'/login'}>Login</NavLink>
        </div>
      </div>
    )
  }, [completed, serverError]);

  const invalidLinkContent = useMemo(() => (
    <div className={styles.confirm}>
      <div className={styles.confirmErrorTitle}>Invalid link</div>
      <div className={styles.confirmSubtitle}>This password reset link is invalid. Please request a new link.</div>
      <div className={styles.footer}>
        Back to <NavLink className={styles.footerLink} to={'/forgot-password'}>Forgot Password</NavLink>
      </div>
    </div>
  ), []);

  return (
    <NewAuthPageLayout backToLogin={true} title={<div>Reset your password</div>} buttonTitle={'Reset password'}
                     onSubmit={handleSubmit}
                     loading={loading}>
      {!hasRequiredParams ? invalidLinkContent : completed ? completedContent :
        <>
          <TextInput allowSpecialCharacters type={'password'} className={styles.input} placeholder={'New password'} label={'New password*'}
                     id={'password'}
                     value={password} onChange={handleChange(setPassword)}/>
          <TextInput allowSpecialCharacters type={'password'} className={styles.input} placeholder={'Confirm password'}
                     label={'Confirm password*'}
                     id={'confirmPassword'} value={confirmPassword} onChange={handleChange(setConfirmPassword)}/>
          {!!clientError && <div className={styles.error}>{clientError}</div>}
          <div className={styles.back}>
            <NavLink className={styles.backLink} to={'/login'}>Back to Login</NavLink>
          </div>
          <div className={styles.arrowDiv}>
            <span className={styles.arrowText}>Need help logging in?<br/>Click to Chat with us!</span>
            <ArrowIcon width="40" height="200" />
          </div>
          <Button disabled={loading} className={styles.resetButton} label={'Reset'} type={'submit'} isLoading={loading} />
        </>
      }
    </NewAuthPageLayout>
  );
}

export default ForgotPasswordConfirm;
